import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Grid,
  Chip,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Close, History, TrendingUp, TrendingDown ,DeviceThermostat,
  FavoriteBorder,
  Favorite,
  Air } from '@mui/icons-material';
import axios from 'axios';
import { getPatientVitalsHistoryAdmission } from '../BaseURL';
import TemperatureChartDialog from './TemperatureChartDialog';
import BloodPressureChartDialog from './BloodPressureChartDialog ';
import PulseChartDialog from './PulseChartDialog';
import RespiratoryRateChartDialog from './RespiratoryRateChartDialog';
import moment from "moment";


const VitalsHistoryDialogAdmission = ({ open, onClose, patientData ,startDate }) => {
  const currentDate = moment().format('YYYY-MM-DD'); // 2025-11-26
  const [loading, setLoading] = useState(false);
  const [vitalsHistory, setVitalsHistory] = useState([]);
  const [error, setError] = useState(null);

   const [openTempChart, setOpenTempChart] = useState(false);
   const [openBP, setOpenBP] = useState(false);
   const [openPulse, setOpenPulse] = useState(false);
   const [openRespChart, setOpenRespChart] = useState(false);


  useEffect(() => {
    if (open && patientData && patientData.id) {
      loadVitalsHistory();
    } else {
      // Reset state when dialog closes
      setVitalsHistory([]);
      setError(null);
    }
  }, [open, patientData]);

  const loadVitalsHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(getPatientVitalsHistoryAdmission, {
        params: { patientId: patientData.id , startDate : startDate ,enddate:currentDate }
      });

      if (response.data.success) {
        setVitalsHistory(response.data.vitalsHistory || []);
      } else {
        setError(response.data.message || 'Failed to load vitals history');
      }
    } catch (err) {
      console.error('Error loading vitals history:', err);
      setError('Failed to load vitals history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  

  // Calculate statistics
  const temps = vitalsHistory.map(v => parseFloat(v.temperature)).filter(t => !isNaN(t));
  const avgTemp = temps.length > 0 ? (temps.reduce((a, b) => a + b, 0) / temps.length).toFixed(1) : 'N/A';
  
  const weights = vitalsHistory.map(v => parseFloat(v.weight)).filter(w => !isNaN(w));
  const avgWeight = weights.length > 0 ? (weights.reduce((a, b) => a + b, 0) / weights.length).toFixed(1) : 'N/A';
  const weightChange = weights.length >= 2 ? (weights[0] - weights[weights.length - 1]).toFixed(1) : 'N/A';

  if (!patientData) return null;

 

  return (
    <>

     <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #19b934ff 0%, #19b934ff 100%)",
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
            <History />
          </Avatar>
          <Box>
            <Typography variant="h6" component="div">
              Vital Signs History
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              {patientData.fullName} ({patientData.id})
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading vitals history...</Typography>
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="error" gutterBottom>
              {error}
            </Typography>
            <Button variant="contained" onClick={loadVitalsHistory} sx={{ mt: 2 }}>
              Retry
            </Button>
          </Box>
        )}

        {/* Main Content */}
        {!loading && !error && (
          <>
            {/* Patient Info Card */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 56,
                    height: 56,
                    fontSize: 20,
                    fontWeight: 700
                  }}
                >
                  {patientData.fullName
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {patientData.fullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {patientData.id} • Age: {patientData.age} • Gender: {patientData.gender} • Tel: {patientData.tel}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Summary Statistics */}
            {vitalsHistory.length > 0 && (
              <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  📊 Vitals Trends Summary
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Avg Temperature
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="primary.main">
                        {avgTemp}°C
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, bgcolor: '#f3e5f5', borderRadius: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Avg Weight
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="secondary.main">
                        {avgWeight} kg
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: weightChange > 0 ? '#e8f5e9' : '#ffebee', 
                      borderRadius: 2, 
                      textAlign: 'center' 
                    }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Weight Change
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        {weightChange !== 'N/A' && (
                          weightChange > 0 ? 
                            <TrendingUp color="success" /> : 
                            <TrendingDown color="error" />
                        )}
                        <Typography 
                          variant="h5" 
                          fontWeight={700}
                          color={weightChange > 0 ? 'success.main' : weightChange < 0 ? 'error.main' : 'text.primary'}
                        >
                          {weightChange !== 'N/A' ? (weightChange > 0 ? '+' : '') + weightChange + ' kg' : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, bgcolor: '#e1f5fe', borderRadius: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Total Records
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="info.main">
                        {vitalsHistory.length}
                      </Typography>
                    </Box>
                  </Grid>
                  {/* === NEW: VITALS CHART BUTTONS WITH ICONS === */}
                <Grid item xs={12} sm={6} md={3}>
                  <Button 
                    variant="contained" 
                    fullWidth
                    startIcon={<DeviceThermostat />}
                      onClick={() => setOpenTempChart(true)}
                    sx={{ 
                      py: 2, 
                      borderRadius: 2, 
                      textTransform: "none", 
                      fontWeight: 600 
                    }}
                  >
                    Temperature Charts
                  </Button>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Button 
                    variant="contained" 
                    color="secondary"
                    fullWidth
                    startIcon={<FavoriteBorder />}
                    onClick={() => setOpenBP(true)}
                    sx={{ 
                      py: 2, 
                      borderRadius: 2, 
                      textTransform: "none", 
                      fontWeight: 600 
                    }}
                  >
                    Blood Pressure Charts
                  </Button>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Button 
                    variant="contained"
                    color="success"
                    fullWidth
                    startIcon={<Favorite />}
                     onClick={() => setOpenPulse(true)}
                    sx={{ 
                      py: 2, 
                      borderRadius: 2, 
                      textTransform: "none", 
                      fontWeight: 600 
                    }}
                  >
                    Pulse Charts
                  </Button>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Button 
                    variant="contained"
                    onClick={() => setOpenRespChart(true)}
                    color="info"
                    fullWidth
                    startIcon={<Air />}
                    sx={{ 
                      py: 2, 
                      borderRadius: 2, 
                      textTransform: "none", 
                      fontWeight: 600 
                    }}
                  >
                    Respiratory Rate Charts
                  </Button>
                </Grid>

                </Grid>
              </Paper>
            )}

            {/* Vitals History Table */}
            <TableContainer component={Paper} variant="outlined">
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>Temp (°C)</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>Pulse (bpm)</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>Resp (/min)</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>BP (mmHg)</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>Weight (kg)</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>Height (cm)</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>BMI</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vitalsHistory.length > 0 ? (
                    vitalsHistory.map((record, idx) => {
                      const temp = parseFloat(record.temperature);
                      const [systolic] = record.bp ? record.bp.split('/').map(Number) : [0];
                      const hasAbnormal = temp > 37.5 || temp < 36 || systolic > 140 || systolic < 90;
                      
                      return (
                        <TableRow 
                          key={idx}
                          hover
                          sx={{ 
                            '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                            bgcolor: hasAbnormal ? 'rgba(255, 152, 0, 0.05)' : 'inherit'
                          }}
                        >
                          <TableCell>{record.date}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {temp > 37.5 && '🔥'}
                              {temp < 36 && '❄️'}
                              {record.temperature || '-'}
                            </Box>
                          </TableCell>
                          <TableCell>{record.pulse || '-'}</TableCell>
                          <TableCell>{record.respiration || '-'}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {systolic > 140 && '⚠️'}
                              {record.bp || '-'}
                            </Box>
                          </TableCell>
                          <TableCell>{record.weight || '-'}</TableCell>
                          <TableCell>{record.height || '-'}</TableCell>
                          <TableCell>{record.bmi || '-'}</TableCell>
                          <TableCell>
                            {hasAbnormal ? (
                              <Chip label="Review" size="small" color="warning" />
                            ) : (
                              <Chip label="Normal" size="small" color="success" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No vital signs history available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ bgcolor: 'grey.50', p: 2 }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
          size="large"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
    

    <TemperatureChartDialog
    open={openTempChart}
    onClose={() => setOpenTempChart(false)}
    vitals={vitalsHistory}  // send REAL data
  />

    <BloodPressureChartDialog
    open={openBP}
    onClose={() => setOpenBP(false)}
    vitals={vitalsHistory}
  />

  <PulseChartDialog
    open={openPulse}
    onClose={() => setOpenPulse(false)}
    vitals={vitalsHistory}
  />

  <RespiratoryRateChartDialog
  open={openRespChart}
  onClose={() => setOpenRespChart(false)}
  vitals={vitalsHistory}
/>

    </>
   

    

  );
};

export default VitalsHistoryDialogAdmission;


